import type { MutableRefObject, RefObject } from "react";
import { useCallback, useEffect, useState } from "react";
import { getScrollHintState } from "./kanban-scroll-helpers";
import type { ScrollHintState } from "./kanban-scroll-types";

interface UseKanbanScrollHintsInput {
  columnCount: number;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  scrollHintAnimationFrameRef: MutableRefObject<number | null>;
}

export function useKanbanScrollHints({
  columnCount,
  scrollContainerRef,
  scrollHintAnimationFrameRef,
}: UseKanbanScrollHintsInput): ScrollHintState {
  const [scrollHintState, setScrollHintState] = useState<ScrollHintState>({
    showLeft: false,
    showRight: false,
  });

  const updateScrollHints = useCallback((): void => {
    const nextScrollHintState = getScrollHintState(scrollContainerRef.current);
    setScrollHintState((previousScrollHintState) => {
      if (
        previousScrollHintState.showLeft === nextScrollHintState.showLeft &&
        previousScrollHintState.showRight === nextScrollHintState.showRight
      ) {
        return previousScrollHintState;
      }

      return nextScrollHintState;
    });
  }, [scrollContainerRef]);

  const scheduleScrollHintUpdate = useCallback((): void => {
    if (typeof window === "undefined") {
      return;
    }

    if (scrollHintAnimationFrameRef.current != null) {
      window.cancelAnimationFrame(scrollHintAnimationFrameRef.current);
    }

    scrollHintAnimationFrameRef.current = window.requestAnimationFrame(() => {
      updateScrollHints();
    });
  }, [scrollHintAnimationFrameRef, updateScrollHints]);

  useEffect(() => {
    const scrollContainerNode = scrollContainerRef.current;
    if (!scrollContainerNode) {
      return undefined;
    }

    scheduleScrollHintUpdate();
    scrollContainerNode.addEventListener("scroll", scheduleScrollHintUpdate, {
      passive: true,
    });
    window.addEventListener("resize", scheduleScrollHintUpdate);

    return () => {
      scrollContainerNode.removeEventListener(
        "scroll",
        scheduleScrollHintUpdate,
      );
      window.removeEventListener("resize", scheduleScrollHintUpdate);

      if (scrollHintAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(scrollHintAnimationFrameRef.current);
        scrollHintAnimationFrameRef.current = null;
      }
    };
  }, [columnCount, scheduleScrollHintUpdate, scrollContainerRef, scrollHintAnimationFrameRef]);

  return scrollHintState;
}
